import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Scissors, Handshake } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useSeo } from "@/hooks/useSeo";
import { PageIntro } from "@/components/site/PageIntro";
import { ErrorState, Spinner } from "@/components/site/Primitives";
import { EditorForm, PartnerForm } from "@/components/apply/Forms";
import { EDITOR_EMPTY, PARTNER_EMPTY, validateEditor, validatePartner, editorPayload } from "@/components/apply/schema";

const KINDS = {
  editor: { label: "Join as an editor", icon: Scissors, empty: EDITOR_EMPTY, validate: validateEditor, payload: editorPayload, Form: EditorForm, blurb: "Freelance and full-time editors: share your showreel, skills, software, rates and availability." },
  partner: { label: "Partner with us", icon: Handshake, empty: PARTNER_EMPTY, validate: validatePartner, payload: (f) => f, Form: PartnerForm, blurb: "Agencies, retainers and creator programmes: apply for an ongoing editing partnership." },
};

const Done = ({ r }) => (
  <div className="py-6" data-testid="apply-confirmation">
    <CheckCircle2 className="text-[#0055FF]" size={36} />
    <h2 className="mt-6 text-3xl font-semibold tracking-[-0.03em]">Application <span className="eb-accent">received.</span></h2>
    <p className="mt-4 text-zinc-400">Reference <span className="font-display text-xl text-[#00E5FF]" data-testid="apply-reference">{r.reference}</span> · {fmtDateTime(r.submitted_at)}</p>
    <p className="mt-3 text-zinc-500 text-sm max-w-xl">We review every application personally and will reach out if there's a fit.</p>
    {r.notifications?.acknowledgement !== "sent" && <p className="mt-3 text-amber-400 text-sm" data-testid="apply-email-warning">Your application is saved, but we couldn't send the confirmation email right now. Please keep your reference.</p>}
  </div>
);

export default function Apply() {
  const [params, setParams] = useSearchParams();
  const kind = params.get("type") === "partner" ? "partner" : "editor";
  const cfg = KINDS[kind];
  const startedAt = useRef(Date.now());
  const [forms, setForms] = useState({ editor: EDITOR_EMPTY, partner: PARTNER_EMPTY });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);
  useSeo("apply", "/apply");
  const form = forms[kind];
  const set = (k, v) => { setForms((f) => ({ ...f, [kind]: { ...f[kind], [k]: v } })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };
  const switchTo = (k) => { setParams({ type: k }); setErrors({}); setResult(null); setSubmitError(null); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = cfg.validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true); setSubmitError(null);
    try {
      const { data } = await api.post(`/applications/${kind}`, { ...cfg.payload(form), started_at: startedAt.current });
      setResult(data);
      setForms((f) => ({ ...f, [kind]: cfg.empty }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { setSubmitError(`${errMsg(err)} Your answers are still in the form — you can try again.`); } finally { setBusy(false); }
  };

  return (
    <div data-testid="apply-page">
      <PageIntro eyebrow="Applications" title="Work with EditBay." body="Editors who want to cut for brands, and agencies or creators who want a long-term editing partner — apply here." />
      <div className="eb-container py-12 grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-4 space-y-3">
          {Object.entries(KINDS).map(([k, c]) => (
            <button key={k} onClick={() => switchTo(k)} aria-pressed={kind === k} className={`w-full text-left p-6 border transition-colors ${kind === k ? "border-[#0055FF] bg-[#0055FF]/10" : "border-[#27272A] hover:border-zinc-500"}`} data-testid={`apply-tab-${k}`}>
              <c.icon size={20} className="text-[#0055FF]" />
              <p className="mt-3 font-display text-xl font-bold">{c.label}</p>
              <p className="mt-1 text-sm text-zinc-500">{c.blurb}</p>
            </button>
          ))}
          <p className="text-xs text-zinc-600 pt-2">Looking to get a video edited? Use the <a href="/enquire" className="underline text-zinc-400">project enquiry form</a>.</p>
        </aside>
        <div className="lg:col-span-8 bg-[#0a0a0a] border border-[#27272A] p-6 md:p-10">
          {result ? <Done r={result} /> : (
            <form onSubmit={submit} noValidate data-testid={`apply-form-${kind}`}>
              <h2 className="text-2xl font-bold mb-8">{cfg.label}</h2>
              <cfg.Form form={form} set={set} errors={errors} />
              {submitError && <div className="mt-8"><ErrorState message={submitError} /></div>}
              <button type="submit" disabled={busy} className="eb-btn-primary mt-10" data-testid="apply-submit-btn">{busy ? <><Spinner />Submitting</> : "Submit application"}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
